/**
 * Punto de montaje del widget de feedback. Es el ÚNICO componente que toca `App.js`.
 *
 * Decide si el widget existe o no. A diferencia de `SupportChatEntry`, no hace fetch propio: el
 * flag ya viaja en el payload de `GetUserModules` que el provider de módulos tiene cacheado, así
 * que preguntarlo de nuevo sería un request extra en cada arranque para un dato que ya está.
 */
import React, {useEffect, useRef} from 'react';
import * as Sentry from '@sentry/react';

import {useFeedbackWidget} from '../../context/UserModulesContext';
import {LocalStorage} from '../../utils/LocalStorage';
import FeedbackService from './FeedbackService';
import {isEmbedded, isSuppressedPath} from '../../utils/floatingWidgets';
import {FeedbackLauncher} from './FeedbackLauncher';
import './FeedbackWidget.scss';

// La regla de dónde no va nada flotante se comparte con las guías in-app. Se re-exporta porque los
// tests del gate del widget la importan desde acá.
export {isSuppressedPath};

export const FeedbackWidgetEntry = () => {
    // `enabled` ya trae las dos mitades de la regla: la agencia tiene la 2.1 Y el usuario la está
    // viendo (en el Dashbo clásico no aparece). Ver `feedbackWidgetVisible`.
    const {enabled, loaded, uiVersion, experienceTier} = useFeedbackWidget();

    // La sesión se resuelve EN CADA RENDER, no en estado.
    //
    // Con `useState` el valor quedaba congelado desde el primer mount y el único camino de
    // actualización era el evento `storage`, que por spec NO dispara en la pestaña que escribió.
    // Como `/login` cuelga del mismo `<App/>` que nunca remonta, y el login y el logout son SPA
    // (`<Navigate/>`, sin reload), eso producía dos bugs de una sola causa:
    //   - tras un login normal el widget no aparecía hasta un F5, y
    //   - tras un logout + login en la misma pestaña el usuario nuevo heredaba el borrador, las
    //     capturas y el audio del anterior.
    // Leerlo en cada render lo mantiene sincronizado sin depender de ningún evento: el provider
    // de módulos re-renderiza al navegar, que es exactamente cuando la sesión puede haber
    // cambiado.
    const userId = LocalStorage.fetchUserId() || null;
    // La agencia entra en la identidad de sesión por el SuperAdmin: cambiar de agencia recarga la
    // página (`window.location.href`), así que el mismo userId puede estar mirando otra agencia.
    const agencyId = LocalStorage.fetchAgencyId() || null;
    const sessionKey = `${userId || 'anon'}:${agencyId || 'none'}`;
    const knownSessionRef = useRef(sessionKey);

    // El efecto va AFUERA del updater de estado: en StrictMode los updaters se invocan dos veces,
    // y aunque `resetForNewSession` es idempotente, un efecto adentro de un updater es una trampa
    // esperando a que deje de serlo.
    useEffect(() => {
        if (knownSessionRef.current === sessionKey) return;
        knownSessionRef.current = sessionKey;
        FeedbackService.resetForNewSession();
    }, [sessionKey]);

    // Login/logout en OTRA pestaña. El listener sigue haciendo falta: sin él, esta pestaña no se
    // entera hasta que algo la re-renderice.
    useEffect(() => {
        const onStorage = () => {
            const current = `${LocalStorage.fetchUserId() || 'anon'}:${LocalStorage.fetchAgencyId() || 'none'}`;
            if (knownSessionRef.current === current) return;
            knownSessionRef.current = current;
            FeedbackService.resetForNewSession();
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    if (!userId) return null;
    if (LocalStorage.isClientUser()) return null;
    if (isEmbedded()) return null;
    if (isSuppressedPath(window.location.pathname)) return null;
    // Sin `loaded` no sabemos si a la agencia le corresponde. Montar antes produce un parpadeo coral
    // en el borde de la pantalla de CUALQUIER agencia.
    if (!loaded) return null;
    if (!enabled) return null;

    return (
        <FeedbackLauncher
            sessionKey={sessionKey}
            uiVersion={uiVersion}
            experienceTier={experienceTier}
        />
    );
};

/**
 * El `ErrorBoundary` global de `App.js` pinta una pantalla completa de "Algo salió mal": sin este
 * boundary local, un bug del widget de feedback tumbaría la aplicación entera. Con `fallback={null}`
 * el widget desaparece en silencio y el error llega a Sentry.
 */
export const FeedbackWidgetEntryBoundary = () => (
    <Sentry.ErrorBoundary fallback={null}>
        <FeedbackWidgetEntry/>
    </Sentry.ErrorBoundary>
);

export default FeedbackWidgetEntryBoundary;
